// Extrait de DataTab.js (handover bloc F1) — JSX, textes et appels API inchangés.
import { useState, useEffect } from "react";
import api from "@/api";
import { Calendar, Activity, AlertTriangle, RefreshCw, TrendingUp, CheckCircle, Target } from "lucide-react";

export default function InsightsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/insights/descriptive");
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors du chargement des insights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-card text-center py-12">
        <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
        <p className="text-slate-600">{error}</p>
        <button onClick={fetchInsights} className="mt-4 px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-900">
          Reessayer
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="insights-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-rose-600" />
            Insights Descriptifs
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Statistiques purement descriptives — aucun modele predictif
          </p>
        </div>
        <button
          onClick={fetchInsights}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
          data-testid="insights-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Metadata banner */}
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Check-ins analyses</span>
            <p className="font-bold text-slate-800">{data.metadata?.total_checkins?.toLocaleString("fr-FR") || 0}</p>
          </div>
          <div>
            <span className="text-slate-500">Patients uniques</span>
            <p className="font-bold text-slate-800">{data.metadata?.total_patients?.toLocaleString("fr-FR") || 0}</p>
          </div>
          <div>
            <span className="text-slate-500">Alertes totales</span>
            <p className="font-bold text-slate-800">{data.metadata?.total_alerts?.toLocaleString("fr-FR") || 0}</p>
          </div>
          <div>
            <span className="text-slate-500">Calcule le</span>
            <p className="font-bold text-slate-800">
              {data.metadata?.computed_at ? new Date(data.metadata.computed_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Key sentences */}
      {data.sentences?.length > 0 && (
        <div className="dash-card">
          <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-rose-500" />
            Observations cles
          </h4>
          <ul className="space-y-3">
            {data.sentences.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                <span className="mt-0.5 w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-rose-100 text-rose-700 text-xs font-bold">
                  {i + 1}
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Alerts by cancer type */}
      {data.critical_alerts_by_cancer?.length > 0 && (
        <div className="dash-card">
          <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Alertes critiques par type de cancer
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left p-3 font-medium text-slate-600">Cancer</th>
                  <th className="text-right p-3 font-medium text-slate-600">Total alertes</th>
                  <th className="text-right p-3 font-medium text-slate-600">Critiques</th>
                  <th className="text-right p-3 font-medium text-slate-600">% critique</th>
                </tr>
              </thead>
              <tbody>
                {data.critical_alerts_by_cancer.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-800">{row.cancer}</td>
                    <td className="p-3 text-right text-slate-600">{row.total_alerts}</td>
                    <td className="p-3 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.critical_alerts > 0 ? "bg-red-100 text-red-700" : "text-slate-400"}`}>
                        {row.critical_alerts}
                      </span>
                    </td>
                    <td className="p-3 text-right font-medium">
                      <span className={row.pct_critical >= 20 ? "text-red-600" : row.pct_critical >= 10 ? "text-orange-600" : "text-slate-600"}>
                        {row.pct_critical}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Symptom severity by treatment */}
      {data.symptom_severity_by_treatment?.length > 0 && (
        <div className="dash-card">
          <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-orange-500" />
            Severite des symptomes par traitement
          </h4>
          <div className="space-y-4">
            {data.symptom_severity_by_treatment.slice(0, 6).map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-800">{item.treatment}</span>
                  <span className="text-xs text-slate-500">{item.checkins} check-ins</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(item.pct_severe_symptoms || {})
                    .sort(([,a],[,b]) => b - a)
                    .map(([symptom, pct]) => (
                    <span
                      key={symptom}
                      className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        pct >= 30 ? "bg-red-100 text-red-700" :
                        pct >= 15 ? "bg-orange-100 text-orange-700" :
                        "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {symptom}: {pct}%
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mean delay + observance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Delay */}
        <div className="dash-card">
          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-500" />
            Delai moyen symptome → alerte critique
          </h4>
          {data.mean_delay_symptom_to_alert_days !== null ? (
            <div className="text-center py-4">
              <p className="text-4xl font-bold text-slate-800">
                {data.mean_delay_symptom_to_alert_days}
              </p>
              <p className="text-sm text-slate-500 mt-1">jours en moyenne</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">Donnees insuffisantes</p>
          )}
        </div>

        {/* Observance */}
        <div className="dash-card">
          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            Observance vs alertes critiques
          </h4>
          {data.observance_vs_critical && Object.keys(data.observance_vs_critical).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(data.observance_vs_critical).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">{key}</span>
                  <div className="text-right">
                    <span className="text-sm font-medium text-slate-800">{val.pct_critical}%</span>
                    <span className="text-xs text-slate-400 ml-2">({val.critical_alerts}/{val.total_checkins})</span>
                  </div>
                </div>
              ))}
              {data.non_observance_risk_ratio && data.non_observance_risk_ratio > 1 && (
                <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                  Non-observance associee a un risque x{data.non_observance_risk_ratio} d'alerte critique
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">Donnees insuffisantes</p>
          )}
        </div>
      </div>

      {/* Symptom → critical % */}
      {data.symptom_to_critical_pct && Object.keys(data.symptom_to_critical_pct).length > 0 && (
        <div className="dash-card">
          <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Target className="w-4 h-4 text-purple-500" />
            Symptome severe (≥2) → % d'alertes critiques
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(data.symptom_to_critical_pct)
              .sort(([,a],[,b]) => b - a)
              .map(([symptom, pct]) => (
              <div key={symptom} className="bg-slate-50 rounded-xl p-3 text-center">
                <p className={`text-2xl font-bold ${
                  pct >= 30 ? "text-red-600" : pct >= 15 ? "text-orange-600" : "text-slate-700"
                }`}>
                  {pct}%
                </p>
                <p className="text-xs text-slate-500 mt-1 capitalize">{symptom}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="text-center text-xs text-slate-400 py-2">
        {data.metadata?.disclaimer}
      </div>
    </div>
  );
}
